export interface AdStateConfig {
    label: string;
    color: { bg: string; text: string };
}

const AD_STATE_CONFIG: Record<string, AdStateConfig> = {
    SUBMITTED: {
        label: 'Submitted',
        color: { bg: '#3b82f6', text: '#ffffff' }  // blue
    },
    AWAITING_MEDIA: {
        label: 'Awaiting Media',
        color: { bg: '#f59e0b', text: '#ffffff' }  // amber
    },
    IN_PROGRESS: {
        label: 'In Progress',
        color: { bg: '#8b5cf6', text: '#ffffff' }  // purple
    },
    IN_PROGESS: {
        label: 'In Progress',
        color: { bg: '#8b5cf6', text: '#ffffff' }  // purple (backend typo variant)
    },
    PENDING_REVIEW: {
        label: 'Pending Review',
        color: { bg: '#f59e0b', text: '#ffffff' }  // amber
    },
    CREATED: {
        label: 'Created',
        color: { bg: '#10b981', text: '#ffffff' }  // green
    },
    FAILED: {
        label: 'Failed',
        color: { bg: '#ef4444', text: '#ffffff' }  // red
    },
    MODERATION_FAILED: {
        label: 'Moderation Failed',
        color: { bg: '#f97316', text: '#ffffff' }  // orange
    },
    DELETING: {
        label: 'Deleting',
        color: { bg: '#6b7280', text: '#ffffff' }  // gray
    }
};

const DEFAULT_CONFIG: AdStateConfig = {
    label: 'Unknown',
    color: { bg: '#6b7280', text: '#ffffff' }
};

export function getAdStateConfig(state: string | undefined): AdStateConfig {
    if (!state) {
        return DEFAULT_CONFIG;
    }

    const upperState = state.toUpperCase();
    if (AD_STATE_CONFIG[upperState]) {
        return AD_STATE_CONFIG[upperState];
    }

    // Fallback: format the state name
    return {
        ...DEFAULT_CONFIG,
        label: state.charAt(0).toUpperCase() + state.slice(1).toLowerCase().replace(/_/g, ' ')
    };
}

export const adStateOptions = [
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'AWAITING_MEDIA', label: 'Awaiting Media' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'PENDING_REVIEW', label: 'Pending Review' },
    { value: 'CREATED', label: 'Created' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'MODERATION_FAILED', label: 'Moderation Failed' },
    { value: 'DELETING', label: 'Deleting' }
];

// Ad Type (User Uploaded vs AI Generated)
export interface AdTypeConfig {
    label: string;
    color: { bg: string; text: string };
}

const AD_TYPE_CONFIG: Record<string, AdTypeConfig> = {
    UPLOAD_MEDIA: {
        label: 'Uploaded',
        color: { bg: '#3b82f6', text: '#ffffff' }  // blue
    },
    GENERATE_WITH_AI: {
        label: 'AI Generated',
        color: { bg: '#8b5cf6', text: '#ffffff' }  // purple
    }
};

const DEFAULT_TYPE_CONFIG: AdTypeConfig = {
    label: 'Unknown',
    color: { bg: '#6b7280', text: '#ffffff' }
};

export function getAdTypeConfig(adType: string | undefined): AdTypeConfig {
    if (!adType) {
        return DEFAULT_TYPE_CONFIG;
    }

    const upperType = adType.toUpperCase();
    if (AD_TYPE_CONFIG[upperType]) {
        return AD_TYPE_CONFIG[upperType];
    }

    return DEFAULT_TYPE_CONFIG;
}

export const adTypeOptions = [
    { value: 'UPLOAD_MEDIA', label: 'Uploaded' },
    { value: 'GENERATE_WITH_AI', label: 'AI Generated' }
];

// Ad Generation Step Configuration
export interface GenerationStepConfig {
    label: string;
    description: string;
    order: number;
}

export const GENERATION_STEP_CONFIG: Record<string, GenerationStepConfig> = {
    GETTING_SECRETS: {
        label: 'Getting secrets',
        description: 'Retrieving API credentials',
        order: 1
    },
    AUTHENTICATING: {
        label: 'Authenticating',
        description: 'Authenticating with services',
        order: 2
    },
    MODERATING_CONTENT: {
        label: 'Moderating content',
        description: 'Checking content for policy compliance',
        order: 3
    },
    GENERATING_IMAGE: {
        label: 'Generating image',
        description: 'Creating your AI-generated image',
        order: 4
    },
    VALIDATING_IMAGE: {
        label: 'Validating images',
        description: 'Verifying image quality and format',
        order: 5
    },
    SCREENING_SAMPLES: {
        label: 'Screening samples',
        description: 'Checking generated samples for policy compliance',
        order: 6
    },
    GETTING_UPLOAD_URL: {
        label: 'Getting upload URL',
        description: 'Preparing storage location',
        order: 7
    },
    UPLOADING_TO_S3: {
        label: 'Uploading',
        description: 'Uploading samples to storage',
        order: 8
    },
    UPDATING_MEDIA_API: {
        label: 'Finalizing',
        description: 'Completing ad creation',
        order: 9
    }
};

const TERMINAL_STEPS = ['SAMPLES_READY', 'COMPLETED', 'FAILED', 'MODERATION_FAILED'];
const TOTAL_ACTIVE_STEPS = 9;

export function getGenerationStepConfig(step: string | undefined): GenerationStepConfig | null {
    if (!step) {
        return null;
    }

    const upperStep = step.toUpperCase();
    return GENERATION_STEP_CONFIG[upperStep] || null;
}

export function getStepProgress(step: string | undefined): { current: number; total: number; percentage: number } | null {
    if (!step) {
        return null;
    }

    const upperStep = step.toUpperCase();
    const config = GENERATION_STEP_CONFIG[upperStep];

    if (!config) {
        return null;
    }

    return {
        current: config.order,
        total: TOTAL_ACTIVE_STEPS,
        percentage: Math.round((config.order / TOTAL_ACTIVE_STEPS) * 100)
    };
}

export function isActiveGenerationStep(step: string | undefined): boolean {
    if (!step) {
        return false;
    }

    const upperStep = step.toUpperCase();
    return !TERMINAL_STEPS.includes(upperStep) && GENERATION_STEP_CONFIG[upperStep] !== undefined;
}
